import { useEffect, useState } from "react";
import { Flame, ThermometerSnowflake, Mail, Phone, Loader2, Sparkles, RefreshCcw, Clock, Activity, MousePointerClick } from "lucide-react";
import { conciergeLeadsSummary, conciergeEngagementSummary } from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

const BAND_META = {
  hot: { label: "Hot", icon: Flame, color: "#DC2626", desc: "Score ≥ 80 — high booking intent" },
  warm: { label: "Warm", icon: Sparkles, color: "#D97706", desc: "Score 50–79 — exploring, captured" },
  researcher: { label: "Researcher", icon: ThermometerSnowflake, color: "#3A7A8C", desc: "Score 20–49 — info-gathering" },
  cold: { label: "Cold", icon: ThermometerSnowflake, color: "#6B7280", desc: "Score < 20 — passing curiosity" },
};

export default function ConciergeAnalyticsDashboard() {
  useSessionRenewal();
  const [data, setData] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, e] = await Promise.all([conciergeLeadsSummary(), conciergeEngagementSummary()]);
      setData(s);
      setEngagement(e);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="concierge-analytics-dashboard">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Concierge</p>
            <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-[#080808]">
              Conversation analytics
            </h1>
            <p className="mt-3 text-[#3C4048] max-w-2xl">
              Real-time view of how the Concierge funnel is performing — lead score
              distribution, capture rates, and follow-up requests. Privacy-first: every
              metric is consent-based and aggregated.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="concierge-analytics-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#e5e7eb] text-[#080808] font-bold uppercase tracking-[0.12em] text-xs hover:border-[#3A7A8C]"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-[#6B7280]" data-testid="concierge-analytics-loading">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div className="bg-[#FEF2F2] border border-[#FCA5A5] text-[#7F1D1D] p-4">
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <KpiCard label="Total leads" value={data.total_leads} testid="kpi-total" />
              <KpiCard
                label="With email"
                value={data.with_email}
                icon={Mail}
                hint={data.total_leads ? `${Math.round((data.with_email / data.total_leads) * 100)}% capture rate` : ""}
                testid="kpi-with-email"
              />
              <KpiCard
                label="Callback requests"
                value={data.followups}
                icon={Phone}
                hint="Visitors who explicitly asked for outreach"
                testid="kpi-followups"
              />
              <KpiCard
                label="Hot leads"
                value={data.bands.hot.count}
                icon={Flame}
                hint={data.bands.hot.count ? `avg score ${data.bands.hot.avg_score}` : "None yet"}
                testid="kpi-hot"
              />
            </div>

            <h2 className="text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C] mb-4">
              Lead score distribution
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {Object.entries(data.bands).map(([key, b]) => {
                const meta = BAND_META[key];
                const Icon = meta?.icon || Sparkles;
                return (
                  <div
                    key={key}
                    className="bg-white border border-[#E5E7EB] p-5"
                    data-testid={`band-${key}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Icon size={16} style={{ color: meta?.color }} />
                      <span className="text-sm font-bold uppercase tracking-[0.15em]" style={{ color: meta?.color }}>
                        {meta?.label}
                      </span>
                    </div>
                    <p className="text-4xl font-bold text-[#080808]">{b.count}</p>
                    <p className="text-xs text-[#6B7280] mt-1">avg score {b.avg_score || 0}</p>
                    <p className="text-[11px] text-[#9CA3AF] mt-2 leading-snug">{meta?.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-white border border-[#E5E7EB] p-6">
              <h3 className="text-sm font-bold text-[#080808] mb-2">What this measures</h3>
              <ul className="text-sm text-[#3C4048] space-y-1.5 leading-relaxed list-disc pl-5">
                <li>Each Concierge conversation accumulates intent signals (questions about booking, pricing, location; mentions of duration and impact; opt-ins and callback requests).</li>
                <li>Signals are weighted and summed into a score — see Hot ≥ 80, Warm 50–79, Researcher 20–49, Cold below.</li>
                <li>Therapists never reach out unless the visitor explicitly opted in via the handoff form (&quot;Callback requests&quot; tile above).</li>
                <li>Email and SMS opt-ins are tracked independently and feed the nurture sequences (when active).</li>
              </ul>
            </div>

            {engagement && (
              <>
                <h2 className="text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C] mt-12 mb-4">
                  Engagement funnel
                </h2>
                <div className="bg-white border border-[#E5E7EB] p-6 mb-10" data-testid="engagement-funnel">
                  <FunnelRow label="Auto-pop shown" value={engagement.funnel.autopop_shown} total={engagement.funnel.autopop_shown} color="#3A7A8C" />
                  <FunnelRow label="Panel opened" value={engagement.funnel.panel_opened} total={engagement.funnel.autopop_shown} color="#5FA8B4" sublabel={`${engagement.rates.autopop_engage_rate}% engagement rate`} />
                  <FunnelRow label="Real conversation (≥ 1 message)" value={engagement.funnel.had_conversation} total={engagement.funnel.autopop_shown} color="#7BC4D2" />
                  <FunnelRow label="Captured email" value={engagement.funnel.captured_email} total={engagement.funnel.had_conversation} color="#D97706" sublabel={`${engagement.rates.conversation_to_capture}% of conversations`} />
                  <FunnelRow label="Requested follow-up" value={engagement.funnel.requested_followup} total={engagement.funnel.captured_email} color="#DC2626" sublabel={`${engagement.rates.capture_to_followup}% of captured`} last />
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mb-10">
                  <KpiCard
                    label="Median dwell on page"
                    value={engagement.dwell_median_ms ? `${(engagement.dwell_median_ms / 1000).toFixed(1)}s` : "—"}
                    icon={Clock}
                    hint={`across ${engagement.samples} events`}
                    testid="kpi-dwell-median"
                  />
                  <KpiCard
                    label="P90 dwell"
                    value={engagement.dwell_p90_ms ? `${(engagement.dwell_p90_ms / 1000).toFixed(1)}s` : "—"}
                    icon={Activity}
                    hint="90% of visitors engaged faster than this"
                    testid="kpi-dwell-p90"
                  />
                  <KpiCard
                    label="Auto-pop dismiss rate"
                    value={`${engagement.rates.autopop_dismiss_rate || 0}%`}
                    icon={MousePointerClick}
                    hint={engagement.rates.autopop_dismiss_rate > 60 ? "High — consider tuning the opener" : "Healthy"}
                    testid="kpi-dismiss-rate"
                  />
                </div>

                <h2 className="text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C] mb-4">
                  Recent activity
                </h2>
                <div className="bg-white border border-[#E5E7EB] divide-y divide-[#F3F4F6]" data-testid="recent-activity">
                  {engagement.recent.length === 0 && (
                    <p className="px-4 py-6 text-sm text-[#6B7280]">No events yet.</p>
                  )}
                  {engagement.recent.map((ev, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="inline-block w-2 h-2 rounded-full shrink-0"
                          style={{ background: ev.event === "panel_opened" ? "#10B981" : ev.event === "panel_closed" ? "#DC2626" : ev.event === "autopop_shown" ? "#5FA8B4" : "#9CA3AF" }}
                        />
                        <span className="font-mono text-xs text-[#080808] shrink-0">{ev.event}</span>
                        {ev.page_path && <span className="text-xs text-[#6B7280] truncate">{ev.page_path}</span>}
                      </div>
                      <span className="text-[11px] text-[#9CA3AF] shrink-0">
                        {ev.dwell_ms ? `${(ev.dwell_ms / 1000).toFixed(1)}s` : ""} · {ev.at ? new Date(ev.at).toLocaleString() : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, hint, testid }) {
  return (
    <div className="bg-white border border-[#E5E7EB] p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B7280]">{label}</span>
        {Icon && <Icon size={14} className="text-[#3A7A8C]" />}
      </div>
      <p className="mt-3 text-4xl font-bold text-[#080808]">{value}</p>
      {hint && <p className="mt-1 text-xs text-[#6B7280]">{hint}</p>}
    </div>
  );
}

function FunnelRow({ label, value, total, color, sublabel, last }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className={last ? "" : "mb-3"}>
      <div className="flex items-baseline justify-between text-sm mb-1">
        <span className="font-medium text-[#080808]">{label}</span>
        <span className="font-mono text-[#080808]">{value}</span>
      </div>
      <div className="h-2 bg-[#F3F4F6] overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      {sublabel && <p className="mt-1 text-[11px] text-[#6B7280]">{sublabel}</p>}
    </div>
  );
}
